import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Box, BusyButton, Button, Flex } from '@lucidhq/lucidium';

import { ROUTES } from '~/constants';
import { CAMPAIGN_STATUS } from '~/impact-measurement/constants';
import { CampaignTypes } from '~/types';
import { t } from '~/utils/i18n';

import { BackToStudiesDialog } from '../BackToStudiesDialog';
import { useCampaignManager } from '../CampaignManagerProvider';
import { StepLayout } from '../Steps/StepLayout';
import { LaunchCampaign } from './Actions/LaunchCampaign';
import { LaunchServiceCampaign } from './Actions/LaunchServiceCampaign';
import { AudienceBlock } from './Blocks/AudienceBlock';
import { CampaignBlock } from './Blocks/CampaignBlock';
import { ConnectCampaignBlock } from './Blocks/ConnectCampaignBlock';
import { CreativesBlock } from './Blocks/CreativesBlock/CreativesBlock';
import { MediaTrackingBlock } from './Blocks/MediaTrackingBlock';
import { ServiceCampaignBlock } from './Blocks/ServiceCampaignBlock';
import { SurveyBlock } from './Blocks/SurveyBlock';
import { ExportPreview, EXPORT_PREVIEW_ID } from './Misc/ExportPreview';
import { ReviewLayout } from './Misc/ReviewLayout';

const getCampaignReviewBlock = ({
  isSoftware,
  isService
}: Record<string, boolean>) => {
  if (isSoftware) {
    return { type: CampaignTypes.SOFTWARE, DetailsBlock: <CampaignBlock /> };
  }

  if (isService) {
    return {
      type: CampaignTypes.SERVICE,
      DetailsBlock: <ServiceCampaignBlock />
    };
  }

  return {
    type: CampaignTypes.CONNECT,
    DetailsBlock: <ConnectCampaignBlock />
  };
};

export const CampaignReviewStep = () => {
  const history = useHistory();
  // @TODO: add in service review content and decorator booleans
  const {
    isService,
    isSoftware,
    isTrackingOnly,
    can,
    isForcedExposure
  } = useCampaignManager();
  const [isBackToStudiesDialogOpen, setIsBackToStudiesDialogOpen] = useState(
    false
  );

  const handleLaunch = () => setIsBackToStudiesDialogOpen(true);
  const { type, DetailsBlock } = getCampaignReviewBlock({
    isSoftware,
    isService
  });

  return (
    <>
      <BackToStudiesDialog
        isOpen={isBackToStudiesDialogOpen}
        onOpenChange={setIsBackToStudiesDialogOpen}
      />
      <Box lss={{ background: 'white' }}>
        <StepLayout.Container>
          <StepLayout.Header
            lss={{ position: 'relative' }}
            title={t('campaignManager.review.header.title')}
            description={t(`campaignManager.review.header.${type}Description`)}
          >
            {!isTrackingOnly && (
              <Box lss={{ position: 'absolute', top: 0, right: 0 }}>
                <ExportPreview />
              </Box>
            )}
          </StepLayout.Header>
          <StepLayout.Content>
            <Flex
              lss={{ flexDirection: 'column', flex: '1' }}
              id={EXPORT_PREVIEW_ID}
            >
              <ReviewLayout.Block
                title={t('campaignManager.review.campaign.title')}
              >
                {DetailsBlock}
              </ReviewLayout.Block>

              <ReviewLayout.Block
                title={t(
                  `campaignManager.review.${
                    isForcedExposure ? 'creatives' : 'mediaTracking'
                  }.title`
                )}
              >
                {isForcedExposure ? <CreativesBlock /> : <MediaTrackingBlock />}
              </ReviewLayout.Block>

              {isSoftware && (
                <>
                  <ReviewLayout.Block
                    title={t('campaignManager.review.survey.title')}
                  >
                    <SurveyBlock />
                  </ReviewLayout.Block>

                  <ReviewLayout.Block
                    title={t('campaignManager.review.audience.title')}
                  >
                    <AudienceBlock />
                  </ReviewLayout.Block>
                </>
              )}

              <Flex lss={{ mb: 'xxxl', justifyContent: 'center' }}>
                {can.edit && (
                  <>
                    {isSoftware && (
                      <LaunchCampaign onLaunch={handleLaunch}>
                        {({ onToggle }) => (
                          <Button onClick={onToggle}>
                            {t('campaignManager.actions.launch')}
                          </Button>
                        )}
                      </LaunchCampaign>
                    )}

                    {isService && (
                      <LaunchServiceCampaign onLaunch={handleLaunch}>
                        {({ onToggle, isLoading }) => (
                          <BusyButton onClick={onToggle} isBusy={isLoading}>
                            {t('campaignManager.actions.launch')}
                          </BusyButton>
                        )}
                      </LaunchServiceCampaign>
                    )}
                  </>
                )}
                {isTrackingOnly && (
                  <Button
                    onClick={() =>
                      history.push(
                        `${ROUTES.adminCampaigns}?status__in=${CAMPAIGN_STATUS['tracking only']}`
                      )
                    }
                  >
                    {t('campaignManager.actions.returnToStudies')}
                  </Button>
                )}
              </Flex>
            </Flex>
          </StepLayout.Content>
        </StepLayout.Container>
      </Box>
    </>
  );
};
